import {
	ActionIcon,
	Collapse,
	Divider,
	Group,
	Paper,
	Stack,
} from '@mantine/core';
import { Prism } from '@mantine/prism';
import { IconChevronDown, IconChevronRight } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';

import { Text, TextInput, Title } from '@repo/foundations';

interface IFeatureFlagLogsProps {
	featureFlagLogs: {
		log: Record<
			string,
			{
				value: string;
				timestamp: string;
				stack_trace: string;
			}
		>;
	};
}

export function FeatureFlagLogs({ featureFlagLogs }: IFeatureFlagLogsProps) {
	const [expandedFlags, setExpandedFlags] = useState<Set<string>>(new Set());
	const [searchTerm, setSearchTerm] = useState('');

	const toggleFlag = (flagKey: string) => {
		const newExpanded = new Set(expandedFlags);
		if (newExpanded.has(flagKey)) {
			newExpanded.delete(flagKey);
		} else {
			newExpanded.add(flagKey);
		}
		setExpandedFlags(newExpanded);
	};

	const hasFeatureFlags =
		featureFlagLogs?.log && Object.keys(featureFlagLogs.log).length > 0;

	// Filter feature flags based on search term
	const filteredFlags = useMemo(() => {
		if (!featureFlagLogs?.log) return [];

		const entries = Object.entries(featureFlagLogs.log);
		if (!searchTerm.trim()) return entries;

		return entries.filter(([flagKey]) =>
			flagKey.toLowerCase().includes(searchTerm.toLowerCase())
		);
	}, [featureFlagLogs?.log, searchTerm]);

	const hasFilteredFlags = filteredFlags.length > 0;

	return (
		<Stack spacing="xs">
			{hasFeatureFlags && (
				<Stack>
					<Title size="md">Feature Flags</Title>
					<TextInput
						placeholder="Search feature flags..."
						value={searchTerm}
						onChange={(event) => setSearchTerm(event.currentTarget.value)}
						iconName="search"
						size="sm"
					/>
					{hasFilteredFlags &&
						filteredFlags.map(([flagKey, flagData]) => {
							const isExpanded = expandedFlags.has(flagKey);

							return (
								<Paper key={flagKey} p="md" withBorder>
									<Stack spacing="xs">
										<Group align="center">
											<Text weight="bold">{flagKey}</Text>
											<ActionIcon
												variant="subtle"
												size="sm"
												onClick={() => toggleFlag(flagKey)}
											>
												{isExpanded ? (
													<IconChevronDown size={16} />
												) : (
													<IconChevronRight size={16} />
												)}
											</ActionIcon>
										</Group>
										<Collapse in={isExpanded}>
											<Stack spacing="xs">
												<Text size="sm" c="dimmed">
													Value: {String(flagData.value)}
												</Text>
												<Text size="sm" c="dimmed">
													{dayjs(flagData.timestamp).format(
														'MMM DD, YYYY [at] HH:mm:ss'
													)}
												</Text>
												<Divider />
												<Text size="sm" weight="bold">
													Stack Trace:
												</Text>
												<Prism
													language="bash"
													colorScheme="dark"
													style={{ fontSize: '12px' }}
												>
													{flagData.stack_trace
														.replace(/\\n/g, '\n')
														.replace(/\\/g, '')}
												</Prism>
											</Stack>
										</Collapse>
									</Stack>
								</Paper>
							);
						})}
					{searchTerm.trim() && !hasFilteredFlags && (
						<Text size="sm" c="dimmed" ta="center">
							No feature flags found matching {searchTerm}
						</Text>
					)}
				</Stack>
			)}
		</Stack>
	);
}
